import { ArrowDown, ArrowUp, ArrowUpDown, MessageSquareOff } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { ContactPill } from "@/components/marketing/contacts/ContactPill";
import { ContactRowMenu } from "@/components/marketing/contacts/ContactRowMenu";
import { formatPhoneNumber } from "@/utils/phoneFormatter";
import {
  describeInvites,
  describeSource,
  describeTextClub,
  formatContactDate,
  type ContactSortKey,
  type ContactsSort,
} from "@/utils/contacts";
import type { ContactListRow } from "@/types/sms";

interface ContactsTableProps {
  rows: ContactListRow[];
  isLoading: boolean;
  sort: ContactsSort;
  onSort: (key: ContactSortKey) => void;
  timezone: string;
  ebrWindowDays: number;
  onOpen: (row: ContactListRow) => void;
  /** Opens the marketing exclusion confirmation; omitted while exclusions are unavailable. */
  onToggleMarketing?: (row: ContactListRow) => void;
}

export const NO_NAME = "No name on file";

/** The roster's "Excluded" indicator: the owner turned off marketing texts. */
export function ExcludedPill() {
  return <ContactPill tone="muted" label="Excluded" icon={MessageSquareOff} srLabel="from marketing texts" />;
}

const SORTABLE: { key: ContactSortKey; label: string }[] = [
  { key: "display_name", label: "Name" },
  { key: "opt_in_status", label: "Text club" },
  { key: "last_order_at", label: "Last order" },
];

function SortHeader({ column, sort, onSort }: { column: ContactSortKey; sort: ContactsSort; onSort: (k: ContactSortKey) => void }) {
  const label = SORTABLE.find((s) => s.key === column)?.label ?? column;
  const active = sort.key === column;
  const Icon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined}
      className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        className={`inline-flex min-h-8 items-center gap-1 uppercase hover:text-foreground ${active ? "text-foreground" : ""}`}
      >
        {label}
        <Icon className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
      </button>
    </th>
  );
}

function PlainHeader({ label, srOnly = false }: { label: string; srOnly?: boolean }) {
  return (
    <th
      scope="col"
      className="whitespace-nowrap px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
    >
      <span className={srOnly ? "sr-only" : undefined}>{label}</span>
    </th>
  );
}

function ordersLine(row: ContactListRow): string | null {
  if (row.total_orders == null) return null;
  return `${row.total_orders} order${row.total_orders === 1 ? "" : "s"}`;
}

/**
 * The roster rows: a sortable table from lg, a two-line list below it (the
 * "Who's in this batch" row pattern) so nothing scrolls sideways on a phone.
 * Both renderings read the same rows and open the same detail sheet.
 */
export function ContactsTable({
  rows,
  isLoading,
  sort,
  onSort,
  timezone,
  ebrWindowDays,
  onOpen,
  onToggleMarketing,
}: ContactsTableProps) {
  const now = new Date();

  if (isLoading) {
    return (
      <div className="rounded-radius-card border border-border bg-card p-3" aria-busy="true">
        <span className="sr-only">Loading contacts</span>
        <div className="space-y-3" aria-hidden="true">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4 lg:grid-cols-[2fr_1.2fr_1fr_1fr]">
              <Skeleton className="h-4" style={{ width: `${60 + ((i * 13) % 30)}%` }} />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="hidden h-4 w-1/2 lg:block" />
              <Skeleton className="hidden h-4 w-2/5 lg:block" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* lg+: table */}
      <div className="hidden overflow-x-auto rounded-radius-card border border-border bg-card lg:block">
        <table className="w-full min-w-[60rem] border-collapse text-sm">
          <thead className="border-b border-border">
            <tr>
              <SortHeader column="display_name" sort={sort} onSort={onSort} />
              <PlainHeader label="Phone" />
              <SortHeader column="opt_in_status" sort={sort} onSort={onSort} />
              <PlainHeader label="Source" />
              <PlainHeader label="Messaging" />
              <SortHeader column="last_order_at" sort={sort} onSort={onSort} />
              <PlainHeader label="Invites" />
              <PlainHeader label="Actions" srOnly />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const name = row.display_name?.trim() || NO_NAME;
              const club = describeTextClub(row, timezone, now);
              const invites = describeInvites(row, ebrWindowDays, timezone, now);
              const lastOrder = formatContactDate(row.last_order_at, timezone, now);
              return (
                <tr key={row.customer_phone} className="border-b border-border/60 align-top last:border-0 hover:bg-muted/40">
                  <td className="max-w-[16rem] px-3 py-2.5">
                    <button
                      type="button"
                      onClick={() => onOpen(row)}
                      className={`block max-w-full truncate text-left font-medium hover:underline ${
                        row.display_name?.trim() ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {name}
                    </button>
                    {row.customer_email && (
                      <span className="block truncate text-xs text-muted-foreground">{row.customer_email}</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-foreground">
                    {formatPhoneNumber(row.customer_phone)}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="flex flex-wrap items-center gap-1">
                      <ContactPill tone={club.tone} label={club.label} />
                      {row.marketing_excluded && <ExcludedPill />}
                    </span>
                    {club.since && <span className="mt-0.5 block text-xs text-muted-foreground">{club.since}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-foreground">{describeSource(row.source, row.source_ref)}</td>
                  <td className="px-3 py-2.5">
                    {row.messaging_status === "disabled" ? (
                      <>
                        <ContactPill tone="danger" label="Disabled" />
                        {row.messaging_disabled_reason && (
                          <span className="mt-0.5 block text-xs text-muted-foreground">{row.messaging_disabled_reason}</span>
                        )}
                      </>
                    ) : (
                      <ContactPill tone="success" label="Enabled" />
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5 tabular-nums text-foreground">
                    {lastOrder ?? "—"}
                    <span className="block text-xs text-muted-foreground">
                      {ordersLine(row) ?? "no orders on record"}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    {invites.pill ? (
                      <ContactPill tone="muted" label={invites.label} icon={false} />
                    ) : (
                      <span className={invites.tone === "success" ? "text-status-success" : invites.tone === "muted" ? "text-muted-foreground" : "text-foreground"}>
                        {invites.label}
                      </span>
                    )}
                    {invites.detail && <span className="block text-xs text-muted-foreground">{invites.detail}</span>}
                  </td>
                  <td className="w-12 px-1 py-0.5 text-right">
                    <ContactRowMenu
                      name={name}
                      phone={row.customer_phone}
                      onViewDetails={() => onOpen(row)}
                      marketingExcluded={row.marketing_excluded}
                      onToggleMarketing={onToggleMarketing ? () => onToggleMarketing(row) : undefined}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* below lg: list */}
      <ul className="divide-y divide-border/60 rounded-radius-card border border-border bg-card lg:hidden">
        {rows.map((row) => {
          const named = !!row.display_name?.trim();
          const name = named ? row.display_name!.trim() : NO_NAME;
          const club = describeTextClub(row, timezone, now);
          const invites = describeInvites(row, ebrWindowDays, timezone, now);
          const lastOrder = formatContactDate(row.last_order_at, timezone, now);
          const meta = [
            formatPhoneNumber(row.customer_phone),
            lastOrder,
            ordersLine(row),
            invites.short,
            row.messaging_status === "disabled" ? "texting disabled" : null,
          ].filter(Boolean);
          return (
            <li key={row.customer_phone} className="flex items-start gap-1 py-1 pl-3 pr-1">
              <button type="button" onClick={() => onOpen(row)} className="min-h-11 min-w-0 flex-1 py-1.5 text-left">
                <span className="flex items-baseline justify-between gap-2">
                  <span className={`truncate text-sm font-medium ${named ? "text-foreground" : "text-muted-foreground"}`}>
                    {name}
                  </span>
                  <ContactPill tone={club.tone} label={club.label} />
                </span>
                <span className="mt-0.5 block break-words text-xs tabular-nums text-muted-foreground">
                  {row.marketing_excluded && (
                    <span className="mr-1.5 inline-block align-middle">
                      <ExcludedPill />
                    </span>
                  )}
                  {meta.join(" · ")}
                </span>
              </button>
              <ContactRowMenu
                name={name}
                phone={row.customer_phone}
                onViewDetails={() => onOpen(row)}
                marketingExcluded={row.marketing_excluded}
                onToggleMarketing={onToggleMarketing ? () => onToggleMarketing(row) : undefined}
              />
            </li>
          );
        })}
      </ul>
    </>
  );
}
